import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Notifications } from "@/components/Notifications";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Package, FileText, ShoppingCart, Truck, Receipt,
  Kanban, Mail, ScrollText, Settings, UserCog, LogOut, Building2, FileStack, Menu, Wallet, FlaskConical, Inbox
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "ventas", "compras", "operaciones"] },
  { to: "/app/pipeline", label: "Pipeline", icon: Kanban, roles: ["admin", "ventas"] },
  { to: "/app/clientes", label: "Clientes (CRM)", icon: Users, roles: ["admin", "ventas", "operaciones"] },
  { to: "/app/productos", label: "Catálogo", icon: Package, roles: ["admin", "ventas", "compras", "operaciones"] },
  { to: "/app/cotizaciones", label: "Cotizaciones", icon: FileText, roles: ["admin", "ventas"] },
  { to: "/app/cotizaciones-web", label: "Solicitudes WEB", icon: Inbox, roles: ["admin", "ventas", "operaciones"] },
  { to: "/app/pedidos", label: "Pedidos", icon: ShoppingCart, roles: ["admin", "ventas", "operaciones"] },
  { to: "/app/proveedores", label: "Proveedores", icon: Truck, roles: ["admin", "compras", "operaciones"] },
  { to: "/app/facturacion", label: "Órdenes a Facturar", icon: Receipt, roles: ["admin", "ventas", "operaciones"] },
  { to: "/app/cobranza", label: "Cobranza", icon: Wallet, roles: ["admin", "ventas", "operaciones"] },
  { to: "/app/facturas", label: "Cuentas x Pagar", icon: Receipt, roles: ["admin", "compras"] },
  { to: "/app/campanas", label: "E-mail Marketing", icon: Mail, roles: ["admin", "ventas"] },
  { to: "/app/plantillas", label: "Plantillas", icon: FileStack, roles: ["admin", "ventas"] },
  { to: "/app/usuarios", label: "Usuarios", icon: UserCog, roles: ["admin"] },
  { to: "/app/auditoria", label: "Auditoría", icon: ScrollText, roles: ["admin"] },
  { to: "/app/ajustes", label: "Ajustes", icon: Settings, roles: ["admin"] },
];

const ROLE_LABEL = { admin: "Administrador", ventas: "Ventas", compras: "Compras", operaciones: "Operaciones" };

function SidebarContent({ items, loc, user, onLogout, onNavigate, prefix = "nav", ws, onToggleWs }) {
  return (
    <div className="flex flex-col h-full bg-primary text-primary-foreground">
      <div className="h-16 flex items-center gap-2 px-6 border-b border-white/10">
        <div className="w-8 h-8 bg-white text-primary grid place-items-center font-heading font-bold rounded-md">W</div>
        <span className="font-heading font-bold tracking-tight">West KAC ERP</span>
        <div className="ml-auto"><Notifications tone="light" /></div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {items.map((n) => {
          const active = loc.pathname === n.to || (n.to !== "/app" && loc.pathname.startsWith(n.to));
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} onClick={onNavigate} data-testid={`${prefix}-${n.label.split(" ")[0].toLowerCase()}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${active ? "bg-white text-primary" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
              <Icon className="w-4 h-4" /> {n.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/10">
        {user?.can_sandbox && (
          <div className={`mb-3 p-2.5 rounded-md ${ws ? "bg-amber-400/25 border border-amber-300/50" : "bg-white/5"}`} data-testid={`${prefix}-practice-wrap`}>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <FlaskConical className="w-4 h-4" />
              <span className="flex-1">Modo Práctica</span>
              <Switch checked={!!ws} onCheckedChange={onToggleWs} data-testid={`${prefix}-practice-toggle`} />
            </label>
            {ws && <p className="text-[11px] text-amber-100/90 mt-1">Datos de prueba · se borran en 5 días</p>}
          </div>
        )}
        <div className="text-sm font-medium">{user?.name}</div>
        <div className="text-xs text-white/50 mb-3">{ROLE_LABEL[user?.role]}</div>
        <Button variant="secondary" size="sm" className="w-full" data-testid="logout-btn" onClick={onLogout}>
          <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
        </Button>
      </div>
    </div>
  );
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ws] = useState(localStorage.getItem("wk_workspace") === "practica");
  const items = NAV.filter((n) => n.roles.includes(user?.role));
  const doLogout = () => { logout(); nav("/login"); };

  const toggleWs = async (checked) => {
    try {
      if (checked) {
        await api.post("/sandbox/ensure", {}, { headers: { "X-Workspace": "practica" } });
        localStorage.setItem("wk_workspace", "practica");
        toast.success("Modo Práctica activado");
      } else {
        localStorage.removeItem("wk_workspace");
        toast.success("Volviste al entorno oficial");
      }
      window.location.href = "/app";
    } catch (e) { toast.error(apiError(e)); }
  };

  // Cierra el menú móvil al cambiar de ruta
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen flex bg-muted/30">
      {/* Sidebar fijo (desktop) */}
      <aside className="w-64 flex-col hidden lg:flex fixed h-screen" data-testid="sidebar">
        <SidebarContent items={items} loc={loc} user={user} onLogout={doLogout} ws={ws} onToggleWs={toggleWs} />
      </aside>

      {/* Drawer móvil / tablet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 border-0 lg:hidden" data-testid="mobile-sidebar">
          <SidebarContent items={items} loc={loc} user={user} onLogout={doLogout} onNavigate={() => setMobileOpen(false)} prefix="mobile-nav" ws={ws} onToggleWs={toggleWs} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 lg:ml-64 min-w-0">
        {ws && (
          <div className="bg-amber-500 text-white text-sm font-medium px-4 py-2 flex items-center gap-2 sticky top-0 z-50" data-testid="practice-banner">
            <FlaskConical className="w-4 h-4 shrink-0" /> Estás en <b>Modo Práctica</b> — todo lo que hagas aquí es de prueba y se elimina automáticamente a los 5 días.
          </div>
        )}
        <header className="h-16 bg-white border-b border-border flex items-center justify-between px-4 sm:px-6 sticky top-0 z-40 lg:hidden">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Abrir menú">
              <Menu className="w-5 h-5" />
            </Button>
            <span className="font-heading font-bold flex items-center gap-2"><Building2 className="w-5 h-5" /> West KAC ERP</span>
          </div>
          <div className="flex items-center gap-2">
            <Notifications tone="dark" />
            <Button variant="ghost" size="icon" onClick={doLogout} aria-label="Cerrar sesión"><LogOut className="w-4 h-4" /></Button>
          </div>
        </header>
        <div className="p-4 sm:p-6 max-w-full overflow-x-hidden">{children}</div>
      </div>
    </div>
  );
}
